import { useEffect, useState } from "react";
import { Row, Col, Card, Button, Typography, Empty, Pagination, Space, Tooltip, message } from "antd";
import { FolderOpenOutlined, UnorderedListOutlined, DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import EstadoFinalizacionBadge from "./EstadoFinalizacionBadge";
import { descargarCarpetaInformeFinal } from "../../../services/informeTecnicoFinal/informeTecnicoFinalService";

const PAGE_SIZE = 12;

const { Paragraph } = Typography;

const NAVY = "#042956";

function ProjectCard({ proyecto, onSelect, onEstadoChange }) {
  const [hovered, setHovered] = useState(false);
  const [descargando, setDescargando] = useState(false);

  const handleDescargar = async (e) => {
    e.stopPropagation();
    setDescargando(true);
    try {
      const blob = await descargarCarpetaInformeFinal(proyecto.oid);
      saveAs(blob, `${proyecto.codigo}_informe_tecnico_final.zip`);
    } catch (error) {
      console.error("Error al descargar la carpeta:", error);
      const status = error.response?.status;
      message.error(
        status === 404
          ? "Este proyecto todavía no tiene documentos para descargar"
          : "No se pudo descargar la carpeta"
      );
    } finally {
      setDescargando(false);
    }
  };

  return (
    <Card
      onClick={() => onSelect?.(proyecto)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      styles={{ body: { padding: 20, display: "flex", flexDirection: "column", height: "100%", } }}
      style={{
        borderRadius: 16,
        padding: 10,
        border: `1px solid ${hovered ? NAVY : "#eef1f5"}`,
        boxShadow: hovered ? "0 10px 24px rgba(4, 41, 86, 0.14)" : "0 1px 4px rgba(4, 41, 86, 0.06)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.25s ease",
        height: "100%",
        minHeight: 200,
        cursor: "pointer"
      }}
    >
      {/* Encabezado: código + estado */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span
          style={{
            background: NAVY,
            color: "#ffffff",
            fontWeight: 700,
            fontSize: 13,
            padding: "4px 12px",
            borderRadius: 8,
            letterSpacing: 0.5
          }}
        >
          {proyecto.codigo}
        </span>
        <Space size={8}>
          <EstadoFinalizacionBadge proyecto={proyecto} onChange={onEstadoChange} />
        </Space>
      </div>
      {/* Título del proyecto */}
      <Paragraph
        ellipsis={{ rows: 2 }}
        style={{ color: NAVY, fontSize: 15, fontWeight: 600, lineHeight: 1.4, marginBottom: 6, minHeight: 42 }}
        title={proyecto.proyecto}
      >
        {proyecto.proyecto}
      </Paragraph>

      {/* Acción */}
      <Space.Compact block style={{ marginTop: "auto" }}>
        <Button
          type="primary"
          size="small"
          icon={<FolderOpenOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(proyecto);
          }}
          style={{
            background: NAVY,
            borderColor: NAVY,
            borderRadius: "8px 0 0 8px",
            height: 32,
            fontSize: 12.5,
            fontWeight: 500,
            flex: 1,
            boxShadow: hovered ? "0 4px 10px rgba(4, 41, 86, 0.25)" : "none",
            transition: "all 0.25s ease"
          }}
        >
          Ver documentos
        </Button>
        <Tooltip title="Descargar la carpeta comprimida (ZIP)">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            loading={descargando}
            onClick={handleDescargar}
            style={{
              borderRadius: "0 8px 8px 0",
              height: 32,
              borderColor: NAVY,
              color: NAVY
            }}
          />
        </Tooltip>
      </Space.Compact>
    </Card>
  );
}

/**
 * ProyectosCards
 * Props:
 *  - proyectos: Array<{ oid, codigo, proyecto, ac_estado, codigo_ProyectoArt,
 *      Nombre_proyectoAsociado, estadoFinalizacion }>
 *  - onSelect(proyecto)
 *  - onEstadoChange(oid, nuevoEstado)
 */
export default function ProyectosCards({ proyectos = [], onSelect, onEstadoChange }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  // Si cambian los filtros (nuevo array), vuelve siempre a la primera página.
  useEffect(() => {
    setCurrentPage(1);
  }, [proyectos]);

  if (!proyectos.length) {
    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          padding: 48,
          border: "1px solid #eef1f5"
        }}
      >
        <Empty description="No se encontraron proyectos con los filtros aplicados" />
      </div>
    );
  }

  const necesitaPaginar = proyectos.length > PAGE_SIZE;
  const visibles = showAll
    ? proyectos
    : proyectos.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <>
      <Row gutter={[20, 20]}>
        {visibles.map((proyecto) => (
          <Col key={proyecto.oid} xs={24} sm={12} md={8} lg={6} xl={6}>
            <ProjectCard proyecto={proyecto} onSelect={onSelect} onEstadoChange={onEstadoChange} />
          </Col>
        ))}
      </Row>

      {necesitaPaginar && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
            marginTop: 28
          }}
        >
          {!showAll && (
            <Pagination
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={proyectos.length}
              onChange={setCurrentPage}
              showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} proyectos`}
            />
          )}
          <Button
            type="text"
            icon={<UnorderedListOutlined />}
            onClick={() => setShowAll((v) => !v)}
            style={{ color: "#042956", fontWeight: 500 }}
          >
            {showAll ? "Volver a paginar" : `Ver los ${proyectos.length} proyectos`}
          </Button>
        </div>
      )}
    </>
  );
}
